import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut, Package } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSelector from './LanguageSelector';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { cartItemCount } = useCart();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 glass-nav border-b border-gray-200">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_mwenkomerce/artifacts/xt1gnrb8_IMG_1789.png" 
              alt="MRC Boutique Logo" 
              className="h-10 w-auto"
            />
            <span className="font-heading text-xl font-bold text-mrc-navy hidden sm:block">
              MRC Boutique, LLC
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/products" 
              className="text-mrc-black hover:text-mrc-navy transition-colors font-medium"
              data-testid="products-nav-link"
            >
              {t('products')}
            </Link>
            <Link 
              to="/products?category=Elektwonik" 
              className="text-mrc-black hover:text-mrc-navy transition-colors font-medium"
            >
              {t('electronics')}
            </Link>
            <Link 
              to="/products?category=Rad" 
              className="text-mrc-black hover:text-mrc-navy transition-colors font-medium"
            >
              {t('clothing')}
            </Link>
            <Link 
              to="/products?category=Kay" 
              className="text-mrc-black hover:text-mrc-navy transition-colors font-medium"
            >
              {t('home')}
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            <LanguageSelector />
            
            {/* Cart */}
            <Link 
              to="/cart" 
              className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="cart-link"
            >
              <ShoppingCart className="h-6 w-6 text-mrc-navy" />
              {cartItemCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 bg-mrc-orange text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {cartItemCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                    <User className="h-6 w-6 text-mrc-navy" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="cursor-pointer" data-testid="orders-link">
                      <Package className="mr-2 h-4 w-4" />
                      {t('myOrders')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="admin-link">
                        <User className="mr-2 h-4 w-4" />
                        {t('adminPanel')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="login-link">
                <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white font-semibold">
                  {t('login')}
                </Button>
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col gap-4">
              <Link 
                to="/products" 
                className="text-mrc-black hover:text-mrc-navy transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('products')}
              </Link>
              <Link 
                to="/products?category=Elektwonik" 
                className="text-mrc-black hover:text-mrc-navy transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('electronics')}
              </Link>
              <Link 
                to="/products?category=Rad" 
                className="text-mrc-black hover:text-mrc-navy transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('clothing')}
              </Link>
              <Link 
                to="/products?category=Kay" 
                className="text-mrc-black hover:text-mrc-navy transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('home')}
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
